/**
 * The project file Godot's glTF importer loads each image of a glTF from, under the default
 * `gltf/embedded_image_handling`, Extract Textures (`editor_scene_importer_gltf.cpp:88`).
 */
import { fileBaseName, getBaseDir, pathJoin, validateFilename } from '../string/string.js';
import { dataUriMimeType, isDataUri } from './gltf.js';
import { imageSourceFormat } from './imageSourceFormat.js';
/**
 * The extension of an extracted image by its MIME type (`gltf_document.cpp:2153-2158`,
 * `gltf_document_extension_texture_webp.cpp:54-56`), else PNG (`gltf_document.cpp:2210`).
 */
function extractedExtension(mimeType) {
    if (mimeType === 'image/jpeg')
        return '.jpg';
    if (mimeType === 'image/webp')
        return '.webp';
    return '.png';
}
/**
 * Each image's file, in `images` order. A name is the image's base name made a valid file name,
 * or its index, with `_<index>` added while it repeats (`gltf_document.cpp:2321-2333`).
 */
export function gltfImageFiles(gltfPath, images) {
    // The extract path is the glTF's base directory (`gltf_document.cpp:7173`, `gltf_state.cpp:414-415`).
    const directory = getBaseDir(gltfPath);
    // `append_from_file` sets the file name to the base name, and `set_filename` takes the base name
    // of that for the prefix (`gltf_document.cpp:7161`, `gltf_state.cpp:440-444`).
    const prefix = fileBaseName(fileBaseName(gltfPath));
    const usedNames = new Set();
    return images.map((image, index) => {
        let name = image.name === undefined ? '' : validateFilename(fileBaseName(image.name));
        if (name === '')
            name = String(index);
        while (usedNames.has(name))
            name += `_${index}`;
        usedNames.add(name);
        const isExternal = image.uri !== undefined && !isDataUri(image.uri);
        const mimeType = image.mimeType ?? dataUriMimeType(image.uri);
        return {
            uri: isExternal ? image.uri : null,
            extracted: pathJoin(directory, `${prefix}_${name}${extractedExtension(mimeType)}`),
        };
    });
}
/**
 * The format Godot's loader gives a glTF image's bytes (`gltf_document.cpp:2138-2177`): by its
 * MIME type, then as a PNG and as a JPEG, for an image whose MIME type is missing or wrong.
 */
export function gltfImageSourceFormat(bytes, mimeType) {
    return ((mimeType === undefined ? null : imageSourceFormat(bytes, mimeType)) ??
        imageSourceFormat(bytes, 'image/png') ??
        imageSourceFormat(bytes, 'image/jpeg'));
}
//# sourceMappingURL=gltfImageFiles.js.map